import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/ui/button';
import { Input, Field } from '@/ui/input';
import { MicroLabel } from '@/ui/micro-label';
import { Wordmark } from '@/domain/wordmark';
import { OAuthButtons } from './../shared/oauth-buttons';

/**
 * Operator console sign-in: split hero. The left panel is brand, the right is the form, and
 * they meet on a hairline. Collapses to the form alone below lg.
 */
export function InternalLogin() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Empty, not prefilled. These fields used to open with a working super_admin address and its
  // password already typed in, which is a convenience on a laptop and a published credential on a
  // deployed host -- the bundle is served to anyone who loads the page, so the value was readable
  // without even submitting the form.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api('/login', {
        method: 'POST',
        realm: 'admin',
        body: JSON.stringify({ email, password, remember_device: rememberDevice })
      });
      await queryClient.invalidateQueries({ queryKey: ['profile'] });
      navigate('/admin');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Sign in failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-screen grid-cols-[1fr_440px] bg-background max-lg:grid-cols-1">
      <section className="flex flex-col justify-between border-r border-border px-14 py-14 max-lg:hidden">
        <Wordmark height={18} />
        <div className="max-w-[420px]">
          <span className="mb-8 block h-0.5 w-10 bg-primary" />
          <h1 className="text-[42px] leading-[1.12] font-medium tracking-tight text-foreground">
            Ground Truth for Physical AI
          </h1>
          <p className="mt-5 text-[14px] leading-relaxed text-muted">
            Data collection, annotation, QA, and evaluation pipelines built for humanoid robotics, VLA and world model
            teams.
          </p>
        </div>
        <MicroLabel size="xs">Capture / QA / Certify / Deliver</MicroLabel>
      </section>

      <section className="flex items-center bg-card px-12 py-12 max-lg:px-6">
        <form onSubmit={submit} className="grid w-full max-w-[288px] gap-4 max-lg:mx-auto">
          <div className="grid gap-1.5">
            <h2 className="text-[22px] font-medium text-foreground">Sign in</h2>
            <p className="text-[13px] text-muted">Access the Miraxis Platform</p>
          </div>

          <OAuthButtons providers={['google', 'microsoft', 'apple']} />

          <div className="flex items-center gap-3">
            <span className="h-px flex-1 bg-border" />
            <MicroLabel size="xs">or</MicroLabel>
            <span className="h-px flex-1 bg-border" />
          </div>

          <Field label="Work email" htmlFor="admin-email">
            <Input
              id="admin-email"
              type="email"
              autoComplete="username"
              placeholder="you@company.ai"
              value={email}
              onChange={event => setEmail(event.target.value)}
              required
            />
          </Field>

          <Field label="Password" htmlFor="admin-password">
            <Input
              id="admin-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={event => setPassword(event.target.value)}
              required
            />
          </Field>

          <div className="flex items-center justify-between">
            <label className="flex cursor-pointer items-center gap-2 text-[13px] text-muted">
              <input
                type="checkbox"
                className="cursor-pointer accent-primary"
                checked={rememberDevice}
                onChange={event => setRememberDevice(event.target.checked)}
              />
              Remember this device
            </label>
            <button
              type="button"
              className="cursor-pointer text-[13px] text-primary hover:underline"
              onClick={() =>
                api('/forgot-password', {
                  method: 'POST',
                  realm: 'admin',
                  body: JSON.stringify({ email })
                }).catch(() => {})
              }
            >
              Forgot password?
            </button>
          </div>

          {error ? <p className="text-[13px] text-danger">{error}</p> : null}

          <Button type="submit" disabled={busy} className="mt-1 w-full">
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>

          <p className="text-center text-[12px] text-muted">No account? Contact your workspace admin.</p>
          <MicroLabel size="xs" caps={false} className="block text-center">
            miraxis.ai · v3.2.1 · SOC 2
          </MicroLabel>
        </form>
      </section>
    </div>
  );
}
